import React, { useMemo } from 'react';
import { FieldInputProps, useFormikContext } from 'formik';
import { FieldConfig } from '../../../services/ValidationService';
import { useAppSelector } from '../../../../reduxService/hooks';
import { RootState } from '../../../../reduxService';
import { FormatSymbol } from '../../../enum/SubscriptSymbol';
import { DecimalSurface, isPendingRawText, resolveDecimalFieldPolicy } from '../../../utils/decimalInput';
import { useDecimalDraft } from '../../../hooks/useDecimalDraft';

interface GeoGridFieldInputProps {
    field: FieldInputProps<any>;
    fieldConfig: FieldConfig;
    fieldName: string;
    disabled?: boolean;
    /** Scalars keep two decimals; geogrid positions keep their precision */
    decimalSurface?: Extract<DecimalSurface, 'geogridScalar' | 'geogridItem'>;
}

const GeoGridFieldInput: React.FC<GeoGridFieldInputProps> = ({ field, fieldConfig, fieldName, disabled = false, decimalSurface = 'geogridScalar' }) => {
    const { setFieldValue } = useFormikContext();
    const isNumberField = fieldConfig.type === 'number';
    const decimalPolicy = useMemo(() => resolveDecimalFieldPolicy(fieldConfig, decimalSurface), [fieldConfig, decimalSurface]);

    // Unusable text goes to Formik as typed so the schema blocks the submit; usable text is stored as a number
    const decimalDraft = useDecimalDraft({
        value: field.value,
        policy: decimalPolicy,
        emptyPolicy: 'publishEmpty',
        resetKey: isPendingRawText(field.value, decimalPolicy) ? 'raw' : 'value',
        onPublish: event => {
            if (event.kind === 'value') setFieldValue(fieldName, event.value);
            else if (event.kind === 'empty') setFieldValue(fieldName, '');
            else setFieldValue(fieldName, event.raw);
        },
    });

    // Get unit system from Redux
    const projectUnitSystem = useAppSelector((state: RootState) => state.Dynamic.project?.data?.unit) as 'Metric' | 'Imperial' | undefined;

    // Calculate display unit based on unitArray and projectUnitSystem
    const displayUnit = useMemo(() => {
        if (!fieldConfig.unit) return '';
        if (typeof fieldConfig.unit === 'string') return fieldConfig.unit;

        // Dynamic unit based on Metric/Imperial
        const index = projectUnitSystem === 'Imperial' ? 1 : 0;
        return fieldConfig.unit[index] || '';
    }, [fieldConfig.unit, projectUnitSystem]);

    const showSymbol = !!fieldConfig.symbol;

    return (
        <div className="field-input-wrapper">
            <div className="input-container">
                {isNumberField ? (
                    <input
                        {...field}
                        {...decimalDraft.inputProps}
                        id={fieldName}
                        name={fieldName}
                        onBlur={event => {
                            decimalDraft.inputProps.onBlur();
                            field.onBlur(event);
                        }}
                        className={displayUnit ? 'input-with-unit' : ''}
                        disabled={disabled}
                    />
                ) : (
                    <input {...field} id={fieldName} name={fieldName} type="text" className={displayUnit ? 'input-with-unit' : ''} disabled={disabled} />
                )}
                {displayUnit && <span className="input-unit-inside">{displayUnit}</span>}
            </div>
            {showSymbol && (
                <span className="input-symbol-outside">
                    <FormatSymbol symbol={fieldConfig.symbol!} />
                </span>
            )}
        </div>
    );
};

export default GeoGridFieldInput;
